import { useCallback, useRef } from 'react';

type JsonLike =
  | null
  | boolean
  | number
  | string
  | JsonLike[]
  | { [key: string]: JsonLike };

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function deepEqual(a: unknown, b: unknown, seen?: WeakMap<object, WeakMap<object, boolean>>): boolean {
  if (Object.is(a, b)) return true;
  if (!isObject(a) || !isObject(b)) return false;

  // Cycle handling: track visited (a,b) pairs.
  const aObj = a as object;
  const bObj = b as object;
  if (!seen) {
    seen = new WeakMap();
  }
  const aSeen = seen.get(aObj);
  if (aSeen?.has(bObj)) {
    return true;
  }
  if (aSeen) {
    aSeen.set(bObj, true);
  } else {
    const map = new WeakMap<object, boolean>();
    map.set(bObj, true);
    seen.set(aObj, map);
  }

  // Arrays
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i += 1) {
      if (!deepEqual(a[i], b[i], seen)) return false;
    }
    return true;
  }

  // Dates (not expected in filter payloads, but safe)
  if (a instanceof Date || b instanceof Date) {
    if (!(a instanceof Date) || !(b instanceof Date)) return false;
    return a.getTime() === b.getTime();
  }

  // Plain objects (filter payloads are JSON-like)
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  for (const key of aKeys) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const aVal = (a as any)[key];
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bVal = (b as any)[key];
    if (!deepEqual(aVal, bVal, seen)) return false;
  }

  return true;
}

function cloneJsonLike<T>(value: T, seen?: WeakMap<object, unknown>): T {
  if (!isObject(value)) {
    return value;
  }

  const valueObj = value as unknown as object;
  if (!seen) {
    seen = new WeakMap();
  }
  const cached = seen.get(valueObj);
  if (cached) {
    return cached as T;
  }

  if (Array.isArray(value)) {
    const arr: unknown[] = [];
    seen.set(valueObj, arr);
    for (const item of value) {
      arr.push(cloneJsonLike(item, seen));
    }
    return arr as unknown as T;
  }

  if (value instanceof Date) {
    const date = new Date(value.getTime());
    seen.set(valueObj, date);
    return date as unknown as T;
  }

  const out: Record<string, unknown> = {};
  seen.set(valueObj, out);
  for (const key of Object.keys(value as Record<string, unknown>)) {
    out[key] = cloneJsonLike((value as Record<string, unknown>)[key], seen);
  }
  return out as T;
}

export function useFilterGuard<TValue>(
  onFilterChange: (value: TValue) => void,
): {
  notifyChange: (newValue: TValue) => boolean;
  syncGuard: (externalValue: TValue) => void;
} {
  const lastNotifiedSnapshotRef = useRef<JsonLike | unknown>(null);

  const notifyChange = useCallback(
    (newValue: TValue) => {
      if (!deepEqual(newValue, lastNotifiedSnapshotRef.current)) {
        // Store a snapshot to guard against accidental mutation of objects.
        lastNotifiedSnapshotRef.current = cloneJsonLike(newValue);
        onFilterChange(newValue);
        return true;
      }
      return false;
    },
    [onFilterChange],
  );

  const syncGuard = useCallback((externalValue: TValue) => {
    lastNotifiedSnapshotRef.current = cloneJsonLike(externalValue);
  }, []);

  return { notifyChange, syncGuard };
}

